import type {
  LoginRequest,
  LoginResponse,
  RegisterRequest,
  RegisterResponse,
  RefreshRequest,
  LogoutRequest,
  SendOtpRequest,
  OtpLoginRequest,
  GetRouteListResponse,
  SyncRoutesRequest,
  PostRoutesResponse,
  ApiError,
  GetProfileResponse,
  UpdateProfileRequest,
  UpdateProfileResponse
} from '@/types/api'
import { apiLogger } from '@/utils/logger'

export interface GoogleLoginRequest {
  id_token: string
}

export type GoogleLoginResponse = LoginResponse

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || ''

class ApiClient {
  private baseUrl: string
  private accessToken: string | null = null

  constructor(baseUrl: string = API_BASE_URL) {
    this.baseUrl = baseUrl
    this.loadToken()
  }

  private loadToken(): void {
    const token = localStorage.getItem('access_token')
    if (token) {
      this.accessToken = token
    }
  }

  setAccessToken(token: string | null): void {
    apiLogger.debug('Setting access token:', token ? `${token.substring(0, 20)}...` : null)
    this.accessToken = token
    if (token) {
      localStorage.setItem('access_token', token)
    } else {
      localStorage.removeItem('access_token')
    }
  }

  getAccessToken(): string | null {
    return this.accessToken
  }

  private async request<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<T> {
    const url = `${this.baseUrl}${endpoint}`
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
      ...options.headers
    }

    if (this.accessToken) {
      (headers as Record<string, string>).Authorization = `Bearer ${this.accessToken}`
    }

    apiLogger.debug('Request:', {
      method: options.method || 'GET',
      endpoint,
      url,
      hasAuth: !!this.accessToken,
      tokenPreview: this.accessToken ? `${this.accessToken.substring(0, 20)}...` : null
    })

    const response = await fetch(url, {
      ...options,
      headers
    })

    apiLogger.debug('Response:', {
      endpoint,
      status: response.status,
      statusText: response.statusText,
      ok: response.ok
    })

    if (!response.ok) {
      let error: ApiError
      try {
        error = await response.json()
      } catch {
        error = { message: `HTTP error ${response.status}: ${response.statusText}` }
      }
      apiLogger.error('Error:', { endpoint, status: response.status, error })
      throw new ApiClientError(error.message, response.status, error)
    }

    // Handle empty responses
    const text = await response.text()
    if (!text) {
      return {} as T
    }

    return JSON.parse(text)
  }

  // Auth endpoints
  async register(data: RegisterRequest): Promise<RegisterResponse> {
    return this.request<RegisterResponse>('/api/auth/register', {
      method: 'POST',
      body: JSON.stringify(data)
    })
  }

  async login(data: LoginRequest): Promise<LoginResponse> {
    return this.request<LoginResponse>('/api/auth/login', {
      method: 'POST',
      body: JSON.stringify(data)
    })
  }

  async refresh(data: RefreshRequest): Promise<LoginResponse> {
    return this.request<LoginResponse>('/api/auth/refresh', {
      method: 'POST',
      body: JSON.stringify(data)
    })
  }

  async logout(data: LogoutRequest): Promise<{ message: string }> {
    return this.request<{ message: string }>('/api/auth/logout', {
      method: 'POST',
      body: JSON.stringify(data)
    })
  }

  async sendOtp(data: SendOtpRequest): Promise<{ message: string }> {
    return this.request<{ message: string }>('/api/auth/otp/send', {
      method: 'POST',
      body: JSON.stringify(data)
    })
  }

  async otpLogin(data: OtpLoginRequest): Promise<LoginResponse> {
    return this.request<LoginResponse>('/api/auth/otp/login', {
      method: 'POST',
      body: JSON.stringify(data)
    })
  }

  async googleLogin(data: GoogleLoginRequest): Promise<GoogleLoginResponse> {
    return this.request<GoogleLoginResponse>('/api/auth/google/login', {
      method: 'POST',
      body: JSON.stringify(data)
    })
  }

  // Profile endpoints
  async getProfile(): Promise<GetProfileResponse> {
    return this.request<GetProfileResponse>('/api/profile', {
      method: 'GET'
    })
  }

  async updateProfile(data: UpdateProfileRequest): Promise<UpdateProfileResponse> {
    return this.request<UpdateProfileResponse>('/api/profile', {
      method: 'PUT',
      body: JSON.stringify(data)
    })
  }

  // Route endpoints
  async listRoutes(): Promise<GetRouteListResponse> {
    return this.request<GetRouteListResponse>('/api/routes/list', {
      method: 'GET'
    })
  }

  async syncRoutes(data: SyncRoutesRequest): Promise<PostRoutesResponse> {
    return this.request<PostRoutesResponse>('/api/routes/sync', {
      method: 'POST',
      body: JSON.stringify(data)
    })
  }

  // Media endpoints
  async uploadImage(waypointId: string, file: File): Promise<{ ImageId: string; WaypointId: string }> {
    const formData = new FormData()
    formData.append('file', file)

    const url = `${this.baseUrl}/api/media/upload?waypointId=${waypointId}`
    const headers: HeadersInit = {}

    if (this.accessToken) {
      (headers as Record<string, string>).Authorization = `Bearer ${this.accessToken}`
    }

    const response = await fetch(url, {
      method: 'POST',
      headers,
      body: formData
    })

    if (!response.ok) {
      let error: ApiError
      try {
        const text = await response.text()
        try {
          error = JSON.parse(text)
        } catch {
          error = { message: text || `HTTP error ${response.status}: ${response.statusText}` }
        }
      } catch {
        error = { message: `HTTP error ${response.status}: ${response.statusText}` }
      }
      throw new ApiClientError(error.message, response.status, error)
    }

    return response.json()
  }

  getImageUrl(imageId: string): string {
    return `${this.baseUrl}/api/media/${imageId}`
  }
}

export class ApiClientError extends Error {
  status: number
  details: ApiError
  emailNotConfirmed?: boolean
  email?: string

  constructor(message: string, status: number, details: ApiError) {
    super(message)
    this.name = 'ApiClientError'
    this.status = status
    this.details = details
    // Check if this is an email_not_confirmed error
    if ('email_not_confirmed' in details && details.email_not_confirmed) {
      this.emailNotConfirmed = true
      this.email = details.email
    }
  }
}

// Export singleton instance
export const apiClient = new ApiClient()

// Export class for testing
export { ApiClient }
